import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { PageHelpButton } from '@/src/renderer/components/common/PageHelpButton'
import { useTranslation } from 'react-i18next'
import { Plus } from 'lucide-react'
import { useScrollbarVisibility } from '@/src/renderer/hooks/useScrollbarVisibility'
import { RecurringIcon } from '../../../../components/icons/RecurringIcon'
import { Button } from '../../../../components/ui/Button'
import { useDataSource } from '../../../../lib/dataSource'
import type { Task } from '../../../../lib/db/models'
import { useSoftAddSound } from '../../../../lib/sounds'
import { useIsActiveTab } from '../../../../pages/TabStack/ActiveTabContext'
import { EmptyState } from '../../components/EmptyState'
import { CalmIllustration } from '../../components/CalmIllustration'
import { SectionHeader } from '../../components/SectionHeader'
import { TaskList, type TaskListHandle, type TaskListSelection } from '../../components/TaskList'
import { SelectionHullScope } from '../../components/SelectionHull'
import { LazyRowScope } from '../../components/LazyRows'
import { TitleRecognitionScope, type TaskEntryData } from '../../components/TaskEntry'
import type { TaskMenuTarget } from '../../components/TaskMenu/TaskMenu.types'
import { useTaskOperations } from '../../hooks/useTaskOperations'
import { useOptimisticTaskOverrides } from '../../hooks/useOptimisticTaskOverrides'
import { useContextMenuMarks } from '../../hooks/useContextMenuMarks'
import { useContextMenuBadgeUpdate } from '../../hooks/useContextMenuBadgeUpdate'
import { useTaskMenuShortcuts, anchorTaskId } from '../../hooks/useTaskMenuShortcuts'
import { useAppTaskMenu, menuAnchorOf, useShowTaskInProject } from '../../hooks/useAppTaskMenu'
import { MoveMenuContext } from '../../utils/moveMenu'
import { useMoveMenu } from '../../hooks/useMoveMenu'
import { useFavoriteShortcut } from '../../hooks/useFavoriteShortcut'
import { usePendingPageJump } from '../../hooks/usePendingPageJump'
import { useCompletionGrace } from '../../hooks/useCompletionGrace'
import { useStableRandomIndex } from '../../hooks/useStableRandomIndex'
import { usePageBreadcrumb } from '../../hooks/usePageBreadcrumb'
import { useScrollFades } from '../../hooks/useScrollFades'
import { duplicateTask } from '../../utils/duplicateTask'
import { orderAtEndOfOwnPlace } from '../../utils/appendTaskOrders'
import { taskToEntryData } from '../../utils/taskToEntryData'
import { splitCompleted } from '../../utils/completedGate'
import { wakeTaskUpdate } from '../../utils/buildTaskUpdate'
import { useRecurringTasks } from './hooks/useRecurringTasks'
import { groupRecurringTasks } from './utils/groupRecurringTasks'
import { RecurringCreator } from './RecurringCreator'
import { consumeRecurringCreateRequest, subscribeRecurringCreateRequest } from './recurringCreateRequest'
import '../TaskViews.scss'
import './RecurringPage.scss'
import { usePlayTask } from '@/src/renderer/lib/focus'

interface RecurringPageProps {
  lifeSpace: 'personal' | 'work' | 'home'
}

type GroupId = 'active' | 'dormant'

/**
 * **Die Seite Wiederkehrend** (CONTEXT.md, Wiederkehrend; entschieden 2026-09-13): alle
 * Aufgaben mit **Wiederholung** eines Lebensbereichs, in den Gruppen **Aktiv** und **Ruhend**,
 * nach nächstem Termin. Die Zeilen sind die normalen Aufgaben-Zeilen; nur die **Marke** trägt
 * hier die Kurzform („3× pro Woche").
 *
 * Aufgebaut wie **Ruhend** (`DormantPage`): optimistische Overrides, Liegezeit, Menü-Kürzel.
 * Jede Gruppe ist eine eigene `TaskList` mit eigener Auswahl; Menü-Aktionen lesen die Auswahl
 * beider.
 */
/** So viele Varianten stehen unter `recurringPage.emptyTitles` in den Sprachdateien. */
const EMPTY_TITLE_COUNT = 8

export const RecurringPage: React.FC<RecurringPageProps> = ({ lifeSpace }) => {
  const { t } = useTranslation()
  const isActive = useIsActiveTab()
  const emptyTitleIndex = useStableRandomIndex(EMPTY_TITLE_COUNT)
  const rawTasks = useRecurringTasks({ lifeSpace, isActive })
  const scrollRef = useRef<HTMLDivElement>(null)
  const { visible: scrollbarVisible, onScroll: handleScrollbar } = useScrollbarVisibility({ ref: scrollRef })
  // Die Spalte um den Scroller trägt die Verläufe oben und unten — wie Projekt,
  // Geschafft und Kurz notiert (#273).
  const listColumnRef = useRef<HTMLDivElement>(null)
  useScrollFades(scrollRef, listColumnRef)
  const titleRef = useRef<HTMLHeadingElement>(null)
  const activeListRef = useRef<TaskListHandle | null>(null)
  const dormantListRef = useRef<TaskListHandle | null>(null)

  /*
    **Die Pfeiltasten kommen über die Gruppengrenze** (Abnahme F-WK2).

    Jede Gruppe ist eine eigene `TaskList` mit eigener Auswahl. Am Ende von
    „Aktiv" war deshalb Schluss, und „Ruhend" erreichte man nur mit der Maus.
    Statt beide Listen zu einer zusammenzulegen — sie sind bewusst getrennt,
    weil sich das Menü und die Aktionen je Gruppe unterscheiden — reichen sie
    sich den Fokus weiter: `↓` in der letzten Zeile der einen geht auf die
    erste der anderen, `↑` in der ersten zurück auf deren letzte.

    Ist die Nachbargruppe zugeklappt oder leer, tut der Griff nichts — die
    Auswahl bleibt dann stehen, wie ohne Nachbarn auch.
  */
  const geheZuRuhend = useCallback(() => {
    dormantListRef.current?.focusFirst()
  }, [])
  const geheZuAktiv = useCallback(() => {
    activeListRef.current?.focusLast()
  }, [])
  const playSoftAdd = useSoftAddSound()
  const dataSource = useDataSource()

  const { optimisticTaskOverrides, setOptimisticTaskOverride } = useOptimisticTaskOverrides(rawTasks)

  // Overrides einrechnen. Wer die Wiederholung abnimmt oder löscht, verlässt die Seite sofort;
  // „Ruhen lassen"/„Aufwecken" wechselt nur die Gruppe (gruppiert wird danach).
  const effectiveTasks = useMemo(() => {
    if (!rawTasks) return rawTasks
    if (optimisticTaskOverrides.size === 0) return rawTasks
    return rawTasks.flatMap(task => {
      const override = optimisticTaskOverrides.get(task.id)
      const merged = override ? { ...task, ...override } : task
      if (merged.deletedAt || merged.recurrence == null) return []
      return [merged]
    })
  }, [rawTasks, optimisticTaskOverrides])

  // ── Die Liegezeit ── wie auf Ruhend: abgehakt bleibt kurz stehen, dann geht die Zeile;
  // der **Nachfolger** steht dann schon da. Kein Geschafft-Schalter (CONTEXT.md).
  const { graceIds, markCompleted } = useCompletionGrace()
  const { active, dormant } = useMemo(
    () => groupRecurringTasks(splitCompleted(effectiveTasks, graceIds).visible),
    [effectiveTasks, graceIds],
  )

  const { handleUpdateTask, handleToggleComplete, handleDeleteTask } = useTaskOperations({
    t, setOptimisticTaskOverride, tasks: effectiveTasks,
  })

  const toggleCompleteWithGrace = useCallback((ids: Set<string>) => {
    markCompleted(ids)
    handleToggleComplete(ids)
  }, [markCompleted, handleToggleComplete])

  /** Die Auswahl beider Listen — Menü-Aktionen gelten für alles Ausgewählte. */
  const getSelection = useCallback(() => new Set([
    ...(activeListRef.current?.getSelection() ?? []),
    ...(dormantListRef.current?.getSelection() ?? []),
  ]), [])

  const handleDuplicate = useCallback(async (clickedTaskId: string) => {
    if (!rawTasks) return
    const selection = getSelection()
    const ids = selection.size > 1 && selection.has(clickedTaskId) ? [...selection] : [clickedTaskId]
    const byId = new Map(rawTasks.map(task => [task.id, task]))
    let created = 0
    for (const id of ids) {
      const source = byId.get(id)
      if (!source) continue
      // Ans Ende ihres Abschnitts, nicht hinter die größte Nummer (Diff-Audit #276).
      await duplicateTask(dataSource, source, { order: await orderAtEndOfOwnPlace(dataSource, source) })
      setTimeout(() => playSoftAdd(), created++ * 80)
    }
  }, [rawTasks, getSelection, playSoftAdd, dataSource])

  const handleContextMenuBadgeUpdate = useContextMenuBadgeUpdate({
    getSelection,
    setOptimisticTaskOverride,
    logTag: 'RecurringPage',
  })

  const getContextMenuMarks = useContextMenuMarks({ getSelection, tasks: rawTasks ?? [] })

  const [menuShortcut, setMenuShortcut] = useState<{ taskId: string; target: TaskMenuTarget } | null>(null)
  const closeMenuShortcut = useCallback(() => setMenuShortcut(null), [])
  // „Verschieben …“ aus dem Rechtsklick öffnet denselben Kasten an der Zeile wie ⇧⌘M (#263).
  const openMoveAtRow = useCallback((taskId: string) => setMenuShortcut({ taskId, target: 'move' }), [])

  const handleRestShortcut = useCallback((taskId: string) => {
    const task = rawTasks?.find(entry => entry.id === taskId)
    if (!task) return
    handleContextMenuBadgeUpdate(taskId, task.pausedAt ? wakeTaskUpdate(task, new Date()) : { pausedAt: new Date() })
  }, [rawTasks, handleContextMenuBadgeUpdate])

  /** **Play** — beginnt einen Fokus an dieser Aufgabe (Zeile, Menü). */
  const handlePlayTask = usePlayTask()

  // ⇧⌘F (#210): der Stern der Auswahl — mit Meldung und Rückgängig.
  // Klick auf eine wiederkehrende Aufgabe ohne Projekt in „Favoriten“ (#210).
  usePendingPageJump({
    page: 'recurring', lifeSpace, isActive, ready: rawTasks !== undefined, rootRef: scrollRef,
    select: taskId => activeListRef.current?.selectTask(taskId),
  })
  const favoriteShortcut = useFavoriteShortcut({ getSelection: getSelection, applyUpdate: handleContextMenuBadgeUpdate })
  const showTaskInProject = useShowTaskInProject()
  useTaskMenuShortcuts({
    enabled: isActive,
    getAnchorTaskId: () => anchorTaskId(
      activeListRef.current?.getFocusedTaskId() ?? dormantListRef.current?.getFocusedTaskId() ?? null,
      [...active, ...dormant].map(task => task.id),
      getSelection(),
    ),
    onMenuShortcut: (taskId, target) => setMenuShortcut({ taskId, target }),
    onFavoriteShortcut: taskId => { if (taskId) void favoriteShortcut(taskId) },
    onRestShortcut: handleRestShortcut,
    onFocusShortcut: handlePlayTask,
    onShowInProjectShortcut: showTaskInProject,
    canMove: true,
  })

  // **Verschieben …** (#257): Rechtsklick, ⇧⌘M und das Menü „Aufgaben“.
  const moveMenu = useMoveMenu(getSelection, openMoveAtRow)

  /*
    **Das Menü „Aufgaben“ in der Menüleiste** (#257). Die beiden Listen melden ihre Auswahl —
    das Menü muss sie kennen, bevor jemand es aufklappt; die Griffe oben lesen sie nur auf Abruf.
  */
  const [activeSelection, setActiveSelection] = useState<TaskListSelection>(NO_SELECTION)
  const [dormantSelection, setDormantSelection] = useState<TaskListSelection>(NO_SELECTION)
  const menuAnchor = useMemo(() => {
    const id = anchorTaskId(
      activeSelection.focusedTaskId ?? dormantSelection.focusedTaskId,
      [...active, ...dormant].map(task => task.id),
      new Set([...activeSelection.selectedIds, ...dormantSelection.selectedIds]),
    )
    return menuAnchorOf(id ? effectiveTasks?.find(task => task.id === id) : null)
  }, [activeSelection, dormantSelection, active, dormant, effectiveTasks])
  useAppTaskMenu({
    enabled: isActive,
    anchor: menuAnchor,
    multiSelected: activeSelection.selectedIds.size + dormantSelection.selectedIds.size > 1,
    canMove: true,
    onMenuShortcut: (taskId, target) => setMenuShortcut({ taskId, target }),
    onComplete: () => {
      activeListRef.current?.completeSelection()
      dormantListRef.current?.completeSelection()
    },
    onFocus: handlePlayTask,
    onRest: handleRestShortcut,
    onFavorite: taskId => { void favoriteShortcut(taskId) },
    onShowInProject: showTaskInProject,
    onDuplicate: () => { if (menuAnchor) void handleDuplicate(menuAnchor.id) },
    onDelete: () => {
      activeListRef.current?.deleteSelection()
      dormantListRef.current?.deleteSelection()
    },
  })

  const projects = dataSource.useLifeSpaceProjects(lifeSpace)
  const projectNameMap = useMemo(
    () => new Map((projects ?? []).map(p => [p.id, p.name])),
    [projects],
  )
  const toEntries = useCallback((tasks: Task[]): TaskEntryData[] => tasks.map(task => {
    const entry = taskToEntryData(task)
    const projectName = task.projectId ? projectNameMap.get(task.projectId) : undefined
    return projectName ? { ...entry, projectName } : entry
  }), [projectNameMap])
  const activeEntries = useMemo(() => toEntries(active), [toEntries, active])
  const dormantEntries = useMemo(() => toEntries(dormant), [toEntries, dormant])

  // ── Gruppen auf- und zuklappen ── nur für diese Sitzung; beide beginnen offen.
  const [collapsed, setCollapsed] = useState<Record<GroupId, boolean>>({ active: false, dormant: false })
  const toggleGroup = useCallback((id: string) => {
    setCollapsed(prev => ({ ...prev, [id]: !prev[id as GroupId] }))
  }, [])

  // ── Anlegen ── erst der Titel, dann der Rhythmus; gespeichert erst nach beidem.
  const [creating, setCreating] = useState(false)
  const openCreator = useCallback(() => setCreating(true), [])
  const closeCreator = useCallback(() => setCreating(false), [])
  const handleCreated = useCallback((taskId: string) => {
    setCreating(false)
    playSoftAdd()
    setCollapsed(prev => ({ ...prev, active: false }))
    requestAnimationFrame(() => activeListRef.current?.selectTask(taskId))
  }, [playSoftAdd])

  // Ablage › „Neue wiederkehrende Aufgabe" — nur der aktive Tab löst den Wunsch ein.
  useEffect(() => {
    if (!isActive) return
    const take = () => { if (consumeRecurringCreateRequest()) openCreator() }
    take()
    return subscribeRecurringCreateRequest(take)
  }, [isActive, openCreator])

  // „A" legt an — nur im aktiven Tab (TabStack-Regel), nicht beim Tippen.
  useEffect(() => {
    if (!isActive || creating) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'a' && e.key !== 'A') return
      if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return
      const target = e.target as HTMLElement | null
      if (target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA' || target?.isContentEditable) return
      e.preventDefault()
      openCreator()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [isActive, creating, openCreator])

  // Beim Aktivwerden die Liste fokussieren (TabStack lässt den Fokus auf <body> fallen).
  useEffect(() => {
    if (!isActive || creating) return
    const rafId = requestAnimationFrame(() => {
      const tag = document.activeElement?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA') return
      activeListRef.current?.focusList()
    })
    return () => cancelAnimationFrame(rafId)
  }, [isActive, creating])

  const isEmpty = rawTasks !== undefined && active.length === 0 && dormant.length === 0 && !creating
  // Der Seitenname in der Breadcrumb: leer fest, sonst sobald die Überschrift weggerollt ist.
  usePageBreadcrumb({
    title: t('recurringPage.title'), isActive, loaded: rawTasks !== undefined, isEmpty, titleRef, scrollerRef: scrollRef,
  })

  if (rawTasks === undefined) {
    return <div className="task-view recurring-page" aria-busy="true" />
  }

  const listProps = {
    onToggleComplete: toggleCompleteWithGrace,
    onDelete: handleDeleteTask,
    onDuplicate: handleDuplicate,
    onShowInProject: showTaskInProject,
    onContextMenuBadgeUpdate: handleContextMenuBadgeUpdate,
    getContextMenuMarks,
    menuShortcut,
    onPlay: handlePlayTask,
    onMenuShortcutClose: closeMenuShortcut,
    onUpdateTask: handleUpdateTask,
    recurrenceDisplay: 'label' as const,
  }

  return (
    <TitleRecognitionScope lifeSpace={lifeSpace}>
    <MoveMenuContext.Provider value={moveMenu}>
    <div className="task-view recurring-page">
      <div ref={listColumnRef} className="recurring-page__list-column">
      <div
        className={`recurring-page__scroll${scrollbarVisible ? ' recurring-page__scroll--scroll-visible' : ''}${isEmpty ? ' recurring-page__scroll--empty' : ''}`}
        ref={scrollRef}
        onScroll={handleScrollbar}
      >
        {/* Im Scroller, nicht darüber: Die Überschrift rollt mit der Liste weg — wie im
            Projekt (#273); „Hinzufügen“ mit ihr, das Kürzel A bleibt. Leer steht sie nicht über
            dem leeren Zustand — der sagt selbst, wo man ist. */}
        {!isEmpty && (
          <div className="recurring-page__header">
            <div className="page-help__row">
              <h1 ref={titleRef} className="recurring-page__title">
                <RecurringIcon size={18} className="recurring-page__title-icon" />
                {t('recurringPage.title')}
              </h1>
              <PageHelpButton page="recurring" />
            </div>
            {/* Wie „Einsortieren“ auf Kurz notiert (#288): Umriss, mittlere Größe, Symbol, Kürzel als kleines Kästchen. */}
            <Button
              variant="outline"
              size="md"
              icon={<Plus size={16} />}
              className="recurring-page__add"
              shortcut={{ kind: 'character', text: 'A' }}
              onClick={openCreator}
              disabled={creating}
            >
              {t('recurringPage.add')}
            </Button>
          </div>
        )}
        {isEmpty ? (
          <EmptyState
            illustration={<CalmIllustration set="desk" />}
            title={t(`recurringPage.emptyTitles.${emptyTitleIndex}`)}
            text={t('recurringPage.emptyText')}
            titleAside={<PageHelpButton page="recurring" />}
            actions={
              <Button variant="primary" shortcut={{ kind: 'character', text: 'A' }} onClick={openCreator}>
                {t('recurringPage.add')}
              </Button>
            }
          />
        ) : (
          // Eine Auswahl-Hülle über beide Gruppen: Die Markierung fährt auch von Aktiv nach Ruhend (#293).
          <div className="recurring-page__groups">
            <SelectionHullScope>
            {/* Ein Budget für alle Listen der Seite (#303): Sonst baute jede ihre ersten Zeilen, auch weit unten. */}
            <LazyRowScope>
            {(active.length > 0 || creating) && (
              <section className="recurring-page__group" aria-label={t('recurringPage.groupActive')}>
                <SectionHeader
                  id="active"
                  title={t('recurringPage.groupActive')}
                  taskCount={active.length}
                  collapsed={collapsed.active}
                  onToggleCollapse={toggleGroup}
                  onRename={noop}
                  onDelete={noop}
                  readonly
                  showWindowModeToggle={false}
                />
                {creating && (
                  <RecurringCreator lifeSpace={lifeSpace} onCreated={handleCreated} onCancel={closeCreator} />
                )}
                <div className="recurring-page__list" hidden={collapsed.active}>
                  <TaskList
                    {...listProps}
                    tasks={activeEntries}
                    handleRef={activeListRef}
                    onSelectionChange={setActiveSelection}
                    stateKey={`recurring-active-${lifeSpace}`}
                    onEscapeBottom={geheZuRuhend}
                  />
                </div>
              </section>
            )}
            {dormant.length > 0 && (
              <section className="recurring-page__group" aria-label={t('recurringPage.groupDormant')}>
                <SectionHeader
                  id="dormant"
                  title={t('recurringPage.groupDormant')}
                  taskCount={dormant.length}
                  collapsed={collapsed.dormant}
                  onToggleCollapse={toggleGroup}
                  onRename={noop}
                  onDelete={noop}
                  readonly
                  showWindowModeToggle={false}
                />
                <div className="recurring-page__list" hidden={collapsed.dormant}>
                  <TaskList
                    {...listProps}
                    tasks={dormantEntries}
                    handleRef={dormantListRef}
                    onSelectionChange={setDormantSelection}
                    stateKey={`recurring-dormant-${lifeSpace}`}
                    onEscapeTop={geheZuAktiv}
                  />
                </div>
              </section>
            )}
            </LazyRowScope>
            </SelectionHullScope>
          </div>
        )}
      </div>
      </div>
    </div>
    </MoveMenuContext.Provider>
    </TitleRecognitionScope>
  )
}

const noop = () => {}
const NO_SELECTION: TaskListSelection = { selectedIds: new Set(), focusedTaskId: null }
